import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, FileText, Info, Loader2 } from "lucide-react";
import { useDocuments, useMatterStages } from "@/hooks/use-supabase-queries";
import { format } from "date-fns";

interface MatterClientViewProps {
  matterId: string;
}

export function MatterClientView({ matterId }: MatterClientViewProps) {
  const { data: documents, isLoading: docsLoading } = useDocuments({ matter_id: matterId });
  const { data: stages, isLoading: stagesLoading } = useMatterStages(matterId);

  if (docsLoading || stagesLoading) {
    return (
      <Card><CardContent className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }

  const visibleDocs = documents?.filter(d => d.visibility === 'all-parties') || [];
  const hiddenDocs = documents?.filter(d => d.visibility !== 'all-parties') || [];
  const stagesWithExplanation = stages?.filter(s => s.client_explanation) || [];

  const visibleCount = visibleDocs.length + stagesWithExplanation.length;
  const hiddenCount = hiddenDocs.length;
  const totalCount = (documents?.length || 0) + (stagesWithExplanation.length);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10"><Eye className="h-5 w-5 text-primary" /></div>
          <div><p className="text-2xl font-bold text-foreground">{visibleCount}</p><p className="text-sm text-muted-foreground">Visible to Client</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-muted"><EyeOff className="h-5 w-5 text-muted-foreground" /></div>
          <div><p className="text-2xl font-bold text-foreground">{hiddenCount}</p><p className="text-sm text-muted-foreground">Hidden from Client</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-accent/10"><Info className="h-5 w-5 text-accent" /></div>
          <div><p className="text-2xl font-bold text-foreground">{totalCount}</p><p className="text-sm text-muted-foreground">Total Items</p></div>
        </div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Client Visibility Controls</CardTitle>
          <p className="text-sm text-muted-foreground">
            Documents with "All Parties" visibility are shown to the client.
          </p>
        </CardHeader>
        <CardContent>
          {!documents || documents.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No documents to manage visibility for.</p>
          ) : (
            <div className="space-y-3">
              {documents.map((doc, index) => (
                <div key={doc.id}>
                  {index > 0 && <Separator className="my-3" />}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded bg-muted/50"><FileText className="h-4 w-4 text-muted-foreground" /></div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm truncate">{doc.name}</p>
                          <Badge variant="outline" className={doc.visibility === 'all-parties' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}>
                            {doc.visibility === 'all-parties' ? 'All Parties' : doc.visibility === 'lawyer-agent' ? 'Lawyer & Agent' : 'Lawyer Only'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {doc.category} • {format(new Date(doc.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm text-muted-foreground hidden sm:block">
                        {doc.visibility === 'all-parties' ? "Visible" : "Hidden"}
                      </span>
                      <Switch checked={doc.visibility === 'all-parties'} disabled />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-accent/30 bg-accent/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-foreground">Client Portal Preview</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Clients see a simplified view of visible items. They can track progress, download shared documents, and view stage updates. Internal notes and hidden documents remain private.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
